import React, { useEffect, useMemo, useState } from "react";
import api from "@api";
import "./PlayerListAuth.css";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";

function PlayerListAuth() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [players, setPlayers] = useState([]);
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  const [allPlayerRegistrations, setAllPlayerRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("");
  const [selectedLeagueFilter, setSelectedLeagueFilter] = useState("");
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState("");
  const [selectedRegistrationFilter, setSelectedRegistrationFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("az");
  const [showDupesOnly, setShowDupesOnly] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [error, setError] = useState("");
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setSelectedRegistrationFilter("");
    setPlayerRegistrations({});

    if (!isAuthenticated || !selectedSeasonFilter) return;

    const fetchRegistrations = async () => {
      try {
        const selectedSeason = seasons.find(
          (season) => Number(season.id) === Number(selectedSeasonFilter)
        );

        const organizationId = selectedSeason?.organization_id;

        const res = await api.get("/api/player-registrations", {
          params: {
            organization_id: organizationId,
            season_id: Number(selectedSeasonFilter),
          },
          withCredentials: true,
        });

        const statuses = {};

        res.data.players.forEach((player) => {
          statuses[player.id] = player.is_registered;
        });

        setPlayerRegistrations(statuses);
      } catch (err) {
        console.error("Error fetching registrations:", err);
      }
    };

    fetchRegistrations();
  }, [selectedSeasonFilter, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchAllRegistrations = async () => {
      try {
        const res = await api.get("/api/player-registrations/all", {
          withCredentials: true,
        });

        setAllPlayerRegistrations(res.data.registrations || []);
        console.log(
          "UNREGISTERED RECORDS:",
          (res.data.registrations || []).filter(
            (r) => r.is_registered === false
          )
        );
      } catch (err) {
        console.error("Error fetching all registrations:", err);
      }
    };

    fetchAllRegistrations();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPlayers = async () => {
      try {
        const response = await api.get("/api/playersv2", {
          withCredentials: true,
        });

        const sortedPlayers = (response.data.players || []).sort((a, b) => {
          const nameA = getPlayerName(a).toLowerCase();
          const nameB = getPlayerName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setPlayers(sortedPlayers);
      } catch (err) {
        console.error("Error fetching players:", err.response?.data || err.message);
        setError("Failed to fetch players");
      }
    };

    fetchPlayers();
  }, [isAuthenticated]);

  useEffect(() => {
    setSelectedLeagueFilter("");
    setSelectedTeamFilter("");
  }, [selectedSeasonFilter]);

  useEffect(() => {
    setSelectedTeamFilter("");
  }, [selectedLeagueFilter]);

  const getPlayerName = (player) =>
    `${player.firstName || ""} ${player.lastName || ""}`.replace(/\s+/g, " ").trim();

  const normalizePlayerName = (player) =>
    getPlayerName(player).toLowerCase().replace(/\s+/g, " ").trim();

  const getRosterEntries = (player) => player.rosters || [];

  const getEntryInfo = (roster) => {
    const slt = roster.seasons_leagues_team || {};
    const seasonLeague = slt.seasons_league || {};
    const team = slt.team || roster.team || {};
    const league = seasonLeague.league || {};
    const season = seasonLeague.season || {};

    return {
      rosterId: roster.id,
      teamId: team.id,
      teamName: team.name || "Unknown Team",
      leagueId: league.id,
      leagueName: league.name || "Unknown League",
      seasonId: season.id,
      seasonName: season.name || "Unknown Season",
      organizationId: season.organization_id,
    };
  };

  const playersWithDuplicates = useMemo(() => {
    const nameCounts = {};

    players.forEach((player) => {
      const key = normalizePlayerName(player);
      if (!key) return;
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    });

    return players.map((player) => {
      const key = normalizePlayerName(player);
      return {
        ...player,
        isDuplicate: key && nameCounts[key] > 1,
      };
    });
  }, [players]);

  const duplicateNameCount = useMemo(() => {
    const nameCounts = {};

    players.forEach((player) => {
      const key = normalizePlayerName(player);
      if (!key) return;
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    });

    return Object.values(nameCounts).filter((count) => count > 1).length;
  }, [players]);

  const seasons = useMemo(() => {
    const seasonMap = new Map();

    players.forEach((player) => {
      getRosterEntries(player).forEach((roster) => {
        const info = getEntryInfo(roster);
        if (info.seasonId && !seasonMap.has(info.seasonId)) {
          seasonMap.set(info.seasonId, {
            name: info.seasonName,
            organization_id: info.organizationId,
          });
        }
      });
    });

    return Array.from(seasonMap.entries())
      .map(([id, season]) => ({
        id,
        name: season.name,
        organization_id: season.organization_id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [players]);



  const leagues = useMemo(() => {
    const leagueMap = new Map();

    players.forEach((player) => {
      getRosterEntries(player).forEach((roster) => {
        const info = getEntryInfo(roster);

        const matchesSeason =
          !selectedSeasonFilter ||
          info.seasonId === Number(selectedSeasonFilter);

        if (matchesSeason && info.leagueId && !leagueMap.has(info.leagueId)) {
          leagueMap.set(info.leagueId, info.leagueName);
        }
      });
    });

    return Array.from(leagueMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [players, selectedSeasonFilter]);

  const teams = useMemo(() => {
    const teamMap = new Map();

    players.forEach((player) => {
      getRosterEntries(player).forEach((roster) => {
        const info = getEntryInfo(roster);

        const matchesSeason =
          !selectedSeasonFilter ||
          info.seasonId === Number(selectedSeasonFilter);

        const matchesLeague =
          !selectedLeagueFilter ||
          info.leagueId === Number(selectedLeagueFilter);

        if (
          matchesSeason &&
          matchesLeague &&
          info.teamId &&
          !teamMap.has(info.teamId)
        ) {
          teamMap.set(info.teamId, info.teamName);
        }
      });
    });

    return Array.from(teamMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [players, selectedSeasonFilter, selectedLeagueFilter]);

  const filteredPlayers = playersWithDuplicates
    .filter((player) => {
      const playerName = getPlayerName(player).toLowerCase();
      const entries = getRosterEntries(player).map(getEntryInfo);

      const matchesSearch = playerName.includes(searchTerm.toLowerCase());

      const matchesDuplicateFilter = !showDupesOnly || player.isDuplicate;
      const matchesUnusedFilter =
        !showUnusedOnly || getRosterEntries(player).length === 0;
      const matchesSeason =
        !selectedSeasonFilter ||
        entries.some((entry) => entry.seasonId === Number(selectedSeasonFilter));

      const matchesLeague =
        !selectedLeagueFilter ||
        entries.some((entry) => entry.leagueId === Number(selectedLeagueFilter));

      const matchesTeam =
        !selectedTeamFilter ||
        entries.some((entry) => entry.teamId === Number(selectedTeamFilter));

      const statuses = selectedSeasonFilter
        ? [playerRegistrations[player.id]]
        : allPlayerRegistrations
          .filter((registration) =>
            Number(registration.player_id) === Number(player.id)
          )
          .map((registration) => registration.is_registered);

      const matchesRegistration =
        !selectedRegistrationFilter ||
        (selectedRegistrationFilter === "registered"
          ? statuses.length === 0 || statuses.some((status) => status === true)
          : statuses.some((status) => status === false));

      return (
        matchesSearch &&
        matchesDuplicateFilter &&
        matchesUnusedFilter &&
        matchesSeason &&
        matchesLeague &&
        matchesTeam &&
        matchesRegistration
      );
    })
    .sort((a, b) => {
      const nameA = getPlayerName(a).toLowerCase();
      const nameB = getPlayerName(b).toLowerCase();

      if (sortOrder === "za") {
        return nameB.localeCompare(nameA);
      }

      return nameA.localeCompare(nameB);
    });

  const duplicateGroups = useMemo(() => {
    const groups = {};

    playersWithDuplicates.forEach((player) => {
      if (!player.isDuplicate) return;

      const key = normalizePlayerName(player);
      if (!groups[key]) groups[key] = [];

      groups[key].push(player);
    });

    return Object.entries(groups).map(([name, groupPlayers]) => {
      const sortedGroup = [...groupPlayers].sort((a, b) => {
        const entryDiff = getRosterEntries(b).length - getRosterEntries(a).length;
        if (entryDiff !== 0) return entryDiff;

        return a.id - b.id;
      });

      return {
        name,
        keepPlayer: sortedGroup[0],
        joinPlayers: sortedGroup.slice(1),
        players: sortedGroup,
      };
    });
  }, [playersWithDuplicates]);

  const handleJoinPlayers = async (keepPlayer, joinPlayers) => {
    const joinIds = joinPlayers.map((player) => player.id);

    if (!keepPlayer?.id || joinIds.length === 0) return;

    const confirmJoin = window.confirm(
      `Join ${joinIds.length} player record${joinIds.length > 1 ? "s" : ""} into "${getPlayerName(keepPlayer)}"?\n\nTeams, leagues, seasons, and player history will stay connected.`
    );

    if (!confirmJoin) return;

    try {
      await api.post(
        "/api/playersv2/merge",
        {
          masterId: keepPlayer.id,
          duplicateIds: joinIds,
        },
        { withCredentials: true }
      );

      const response = await api.get("/api/playersv2", {
        withCredentials: true,
      });

      setPlayers(response.data.players || []);
      alert("Players joined successfully.");
    } catch (err) {
      console.error("Error joining players:", err.response?.data || err.message);
      alert("Failed to join players.");
    }
  };

  const handleDeleteUnusedPlayer = async (player) => {
    const entries = getRosterEntries(player);

    if (entries.length > 0) {
      alert("This player has roster history. Join this player instead of deleting.");
      return;
    }

    const confirmDelete = window.confirm(
      `Delete unused player "${getPlayerName(player)}"?\n\nThis player is not connected to any team, roster, game, or season.`
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/api/playersv2/${player.id}`, {
        withCredentials: true,
      });

      setPlayers((prev) => prev.filter((p) => p.id !== player.id));
      alert("Unused player deleted.");
    } catch (err) {
      console.error("Error deleting player:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to delete player.");
    }
  };

  return (
    <div className="playerList_auth">
      <div className="playerList-header">
        <div>
          <p className="playerList-eyebrow">Admin</p>
          <h2>Players</h2>
          <p className="playerList-subtitle">
            Manage player records and review where each player is rostered.
          </p>
        </div>

        <div className="playerList-summary">
          <div>
            <span>{filteredPlayers.length}</span>
            <p>Showing Players</p>
          </div>
          <div>
            <span>{duplicateNameCount}</span>
            <p>All Duplicate Names</p>
          </div>
        </div>
      </div>

      {error && <p className="playerList-error">{error}</p>}

      <div className="playerList-toolbar">
        <input
          type="text"
          placeholder="Search player name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          value={selectedSeasonFilter}
          onChange={(e) => setSelectedSeasonFilter(e.target.value)}
          className="playerList-filter-select"
        >
          <option value="">All Seasons</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {season.name}
            </option>
          ))}
        </select>
        <select
          value={selectedRegistrationFilter}
          onChange={(e) => setSelectedRegistrationFilter(e.target.value)}
          className="playerList-filter-select"

        >
          <option value="">All Registrations</option>
          <option value="registered">Registered</option>
          <option value="unregistered">Unregistered</option>
        </select>
        <select
          value={selectedLeagueFilter}
          onChange={(e) => setSelectedLeagueFilter(e.target.value)}
          className="playerList-filter-select"
        >
          <option value="">All Leagues</option>
          {leagues.map((league) => (
            <option key={league.id} value={league.id}>
              {league.name}
            </option>
          ))}
        </select>

        <select
          value={selectedTeamFilter}
          onChange={(e) => setSelectedTeamFilter(e.target.value)}
          className="playerList-filter-select"
        >
          <option value="">All Teams</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>

        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="playerList-filter-select"
        >
          <option value="az">A-Z</option>
          <option value="za">Z-A</option>
        </select>

        <label className="playerList-dupe-toggle">
          <input
            type="checkbox"
            checked={showDupesOnly}
            onChange={(e) => setShowDupesOnly(e.target.checked)}
          />
          <span>Show duplicates </span>
        </label>
        <label className="playerList-dupe-toggle">
          <input
            type="checkbox"
            checked={showUnusedOnly}
            onChange={(e) => setShowUnusedOnly(e.target.checked)}
          />
          <span>Show unused players </span>
        </label>
      </div>

      <div className="playerList-dupe-status">
        {duplicateNameCount > 0
          ? `⚠️ Found ${duplicateNameCount} duplicate player name${duplicateNameCount > 1 ? "s" : ""
          }`
          : "✅ No duplicate player names detected"}
      </div>

      {showDupesOnly && duplicateGroups.length > 0 && (
        <div className="playerList-join-groups">
          {duplicateGroups.map((group) => {
            const keepEntries = getRosterEntries(group.keepPlayer).map(getEntryInfo);

            return (
              <div key={group.name} className="playerList-join-card">
                <div className="playerList-join-card-header">
                  <div>
                    <h3>{getPlayerName(group.keepPlayer)}</h3>
                    <p>{group.players.length} matching player records found</p>
                  </div>

                  <button
                    type="button"
                    className="playerList-join-btn"
                    disabled={group.joinPlayers.length === 0}
                    onClick={() =>
                      handleJoinPlayers(group.keepPlayer, group.joinPlayers)
                    }
                  >
                    Join Players
                  </button>
                </div>

                <div className="playerList-join-layout">
                  <div className="playerList-keep-box">
                    <p className="playerList-box-label">Keep this player</p>

                    <div className="playerList-join-player-row">
                      <span>{getPlayerName(group.keepPlayer)}</span>
                      <small>
                        {getRosterEntries(group.keepPlayer).length} player entries
                      </small>
                    </div>

                    {keepEntries.slice(0, 3).map((entry) => (
                      <div key={entry.rosterId} className="playerList-join-detail">
                        {entry.teamName} — {entry.leagueName} — {entry.seasonName}
                      </div>
                    ))}
                  </div>

                  <div className="playerList-join-box">
                    <p className="playerList-box-label">Join these into it</p>

                    {group.joinPlayers.map((player) => {
                      const entries = getRosterEntries(player).map(getEntryInfo);

                      return (
                        <div key={player.id} className="playerList-join-player-block">
                          <div className="playerList-join-player-row">
                            <span>{getPlayerName(player)}</span>
                            <small>{entries.length} player entries</small>
                          </div>

                          {entries.slice(0, 3).map((entry) => (
                            <div
                              key={entry.rosterId}
                              className="playerList-join-detail"
                            >
                              {entry.teamName} — {entry.leagueName} —{" "}
                              {entry.seasonName}
                            </div>
                          ))}

                          {entries.length === 0 && (
                            <div className="playerList-join-detail">
                              Not rostered yet. This record will be removed after
                              joining.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!showDupesOnly && (
        <div className="playerList-table-wrap">
          <table className="playerList-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Player Entries</th>
                <th>Latest Team</th>
                <th>Teams / Leagues / Seasons</th>
                <th>Manage</th>
              </tr>
            </thead>

            <tbody>
              {filteredPlayers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="playerList-empty">
                    No players found
                  </td>
                </tr>
              ) : (
                filteredPlayers.map((player) => {
                  const entries = getRosterEntries(player).map(getEntryInfo);
                  const latestEntry = entries[0];
                  const isOpen = !!expandedRows[player.id];
                  const shownEntries = isOpen ? entries : entries.slice(0, 2);

                  return (
                    <tr key={player.id}>
                      <td>
                        <NavLink
                          to={`/dashboard/players/${player.id}`}
                          className={
                            player.isDuplicate
                              ? "playerList-name playerList-name-dupe"
                              : "playerList-name"
                          }
                        >
                          {getPlayerName(player) || "Unnamed Player"}
                          {player.isDuplicate && " ⚠️"}
                        </NavLink>
                      </td>

                      <td>{entries.length}</td>

                      <td>
                        {latestEntry?.teamName ? (
                          <span>{latestEntry.teamName}</span>
                        ) : (
                          <span className="playerList-muted">No team</span>
                        )}
                      </td>

                      <td>
                        {entries.length > 0 ? (
                          <div className="playerList-entry-list">
                            {shownEntries.map((entry) => (
                              <div
                                key={entry.rosterId}
                                className="playerList-entry"
                              >
                                <span>
                                  {entry.teamName} — {entry.leagueName} —{" "}
                                  {entry.seasonName}
                                </span>
                              </div>
                            ))}

                            {entries.length > 2 && (
                              <button
                                type="button"
                                className="playerList-show-more-btn"
                                onClick={() =>
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [player.id]: !isOpen,
                                  }))
                                }
                              >
                                {isOpen
                                  ? "Show less"
                                  : `+${entries.length - 2} more`}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="playerList-muted">Not rostered yet</span>
                        )}
                      </td>

                      <td>
                        <div className="playerList-actions">
                          <NavLink
                            className="playerList-manage-btn"
                            to={`/dashboard/players/${player.id}`}
                          >
                            Manage
                          </NavLink>

                          {getRosterEntries(player).length === 0 && (
                            <button
                              type="button"
                              className="playerList-delete-unused-btn"
                              onClick={() => handleDeleteUnusedPlayer(player)}
                            >
                              Delete Unused
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PlayerListAuth;